import { FloatingIndicator } from "@mantine/core";
import { componentWrapper } from "../../utilities";
import styles from "./floating-tabs.module.scss";
import type { FloatingTabsFactory, IProps } from "./floating-tabs.types";
import { useEffect, useState } from "react";
import { Tabs } from "../tabs";
import classNames from "classnames";

/**
 * Floating tabs component with an animated indicator that follows the active tab.
 * @param {Array} props.tabs - Array of tab configuration objects with value and label.
 * @param {React.ReactNode} props.children - Tab panel content.
 * @param {string} [props.defaultValue] - The default active tab value (uncontrolled mode).
 * @param {string | null} [props.value] - Controlled active tab value; when provided the indicator syncs to it on every change.
 * @returns {JSX.Element} The FloatingTabs component.
 */
const FloatingTabs = componentWrapper<FloatingTabsFactory, IProps>(
    "FloatingTabs",
    ({ tabs, children, defaultValue, ...otherProps }, ref) => {
        const [rootRef, setRootRef] = useState<HTMLDivElement | null>(null);
        const [value, setValue] = useState<string | null>(otherProps.value ?? defaultValue ?? tabs?.[0]?.value ?? null);
        const [controlsRefs, setControlsRefs] = useState<Record<string, HTMLButtonElement | null>>({});
        const setControlRef = (val: string) => (node: HTMLButtonElement) => {
            controlsRefs[val] = node;
            setControlsRefs(controlsRefs);
        };

        useEffect(() => {
            if (otherProps.value !== undefined) {
                setValue(otherProps.value);
            }
        }, [otherProps.value]);

        // In controlled mode, prefer the parent value so the indicator and Tabs agree
        // on the same render — without this the indicator lags one frame behind a
        // value change because the sync effect fires after commit.
        const displayValue = otherProps.value !== undefined ? otherProps.value : value;

        return (
            <Tabs
                variant="pills-solid"
                value={value}
                onChange={setValue}
                ref={ref}
                classNames={{
                    root: styles["root"],
                    list: styles["list"],
                    panel: styles["panel"],
                    tab: styles["tab"],
                    tabLabel: styles["tabLabel"],
                    tabSection: styles["tabSection"],
                }}
                {...otherProps}
            >
                <Tabs.List ref={setRootRef}>
                    {tabs.map((tab) => (
                        <Tabs.Tab
                            p="sm"
                            key={tab.value}
                            ref={setControlRef(tab.value)}
                            value={tab.value}
                            {...tab.additionalProps}
                        >
                            {tab.label}
                        </Tabs.Tab>
                    ))}
                    <FloatingIndicator
                        target={displayValue ? controlsRefs[displayValue] : null}
                        parent={rootRef}
                        className={classNames(styles["indicator"], "floating-tabs__indicator")}
                    />
                </Tabs.List>
                {children}
            </Tabs>
        );
    }
);

FloatingTabs.Panel = Tabs.Panel;
FloatingTabs.List = Tabs.List;

export { FloatingTabs };
